import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const ClientPortalUsersPage = () => {
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        group_id: '',
        password: '',
        is_active: true
    });
    const [error, setError] = useState('');

    useEffect(() => {
        loadUsers();
        loadGroups();
    }, []);

    const getAuthHeaders = () => {
        const token = localStorage.getItem('accessToken');
        return {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
        };
    };

    const loadGroups = async () => {
        try {
            const response = await fetch(`${API_URL}/groups`, {
                headers: getAuthHeaders()
            });
            if (response.ok) {
                const data = await response.json();
                setGroups(Array.isArray(data) ? data : (data.groups || []));
            }
        } catch (err) {
            console.error('Error loading groups:', err);
        }
    };

    const loadUsers = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/users`, {
                headers: getAuthHeaders()
            });
            if (response.ok) {
                const data = await response.json();
                setUsers(data.users || []);
            } else {
                throw new Error('Failed to fetch users');
            }
        } catch (err) {
            console.error('Error loading users:', err);
            setError('Failed to load users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const clientUsers = (users || []).filter((u) => u.role === 'client');

    const handleAddUser = () => {
        setEditingId(null);
        setFormData({
            email: '',
            first_name: '',
            last_name: '',
            group_id: '',
            password: '',
            is_active: true
        });
        setError('');
        setIsModalOpen(true);
    };

    const handleEditUser = (user) => {
        setEditingId(user.id);
        setFormData({
            email: user.email,
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            group_id: user.group_id ? String(user.group_id) : '',
            password: '',
            is_active: user.is_active
        });
        setError('');
        setIsModalOpen(true);
    };

    const handleSaveUser = async (e) => {
        e.preventDefault();
        setError('');
        if (!formData.group_id) {
            setError('Please select a client group.');
            return;
        }
        if (!editingId) {
            const pwd = (formData.password || '').trim();
            if (pwd.length < 8) {
                setError('Password is required for new client users (min 8 characters).');
                return;
            }
        }
        setSaving(true);
        try {
            const url = editingId ? `${API_URL}/users/${editingId}` : `${API_URL}/users`;
            const payload = {
                email: formData.email,
                first_name: formData.first_name,
                last_name: formData.last_name,
                role: 'client',
                group_id: formData.group_id,
                is_active: formData.is_active
            };
            if (!editingId) {
                payload.password = (formData.password || '').trim();
            } else if ((formData.password || '').trim().length >= 8) {
                payload.password = (formData.password || '').trim();
            }
            const response = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                await loadUsers();
                setIsModalOpen(false);
                setEditingId(null);
            } else {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to save user');
            }
        } catch (err) {
            console.error('Error saving user:', err);
            setError(err.message || 'Failed to save user');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteUser = async (user) => {
        if (!window.confirm(`Delete client user "${user.email}"?`)) return;
        try {
            setSaving(true);
            const response = await fetch(`${API_URL}/users/${user.id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });
            if (response.ok) {
                await loadUsers();
            } else {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to delete user');
            }
        } catch (err) {
            console.error('Error deleting user:', err);
            alert(`Failed to delete user: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingId(null);
        setError('');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-xl">Loading client users...</div>
            </div>
        );
    }

    return (
        <div>
            <div className="mb-6 flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Client Portal users</h2>
                    <p className="text-gray-600 mt-1">
                        Create and manage user accounts for your clients (e.g. Ancaster). These users can log in to the event portal to view reports, appointments, and their group&apos;s data only—separate from internal team access.
                    </p>
                </div>
                <button
                    onClick={handleAddUser}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus size={20} />
                    Add client user
                </button>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client group</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {clientUsers.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                    No client users yet. Add users so your clients can access the event portal (reports, appointments) for their group.
                                </td>
                            </tr>
                        ) : (
                            clientUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{user.email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {user.first_name || user.last_name
                                                ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                                                : '-'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="text-sm text-gray-900">{user.group_name || user.group_id || '-'}</span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                            user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                            {user.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatDate(user.last_login_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={() => handleEditUser(user)}
                                            className="text-blue-600 hover:text-blue-900 mr-4"
                                        >
                                            <Edit size={18} className="inline" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteUser(user)}
                                            className="text-red-600 hover:text-red-900"
                                        >
                                            <Trash2 size={18} className="inline" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
                        <div className="p-6 flex flex-col min-h-0 flex-1 overflow-hidden">
                            <h2 className="text-2xl font-bold mb-4">
                                {editingId ? 'Edit client user' : 'Add client user'}
                            </h2>
                            {error && (
                                <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg shrink-0">
                                    {error}
                                </div>
                            )}
                            <form onSubmit={handleSaveUser} className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                    <input
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="user@client.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
                                    <input
                                        type="text"
                                        value={formData.first_name}
                                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="John"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
                                    <input
                                        type="text"
                                        value={formData.last_name}
                                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Doe"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Client group *</label>
                                    <select
                                        required
                                        value={formData.group_id}
                                        onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                    >
                                        <option value="">Select a client (e.g. Ancaster)</option>
                                        {groups.length === 0 ? (
                                            <option value="" disabled>Loading groups…</option>
                                        ) : (
                                            groups.map((g) => (
                                                <option key={g.id} value={g.id}>{g.name}</option>
                                            ))
                                        )}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Password {editingId ? '(leave blank to keep current)' : '*'}
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder={editingId ? '••••••••' : 'Min 8 characters'}
                                        minLength={editingId ? undefined : 8}
                                        autoComplete={editingId ? 'new-password' : 'new-password'}
                                    />
                                </div>
                                {editingId && (
                                    <div>
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_active}
                                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                                className="rounded border-gray-300"
                                            />
                                            <span className="text-sm font-medium text-gray-700">Active</span>
                                        </label>
                                    </div>
                                )}
                                <div className="flex justify-end gap-4 mt-6">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : editingId ? 'Update user' : 'Create user'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientPortalUsersPage;
